import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { incidents } from '@cwos/api-client';
import { Pill, Refused, ScopeNote, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsHeader } from '../settings/common.js';
import {
  CATEGORY_READING,
  SEVERITY_READING,
  STATUS_READING,
  emptyIncidentSentence,
  formatWhen,
  incidentPopulation,
  incidentScopeNote,
  notificationReading,
  participantSentence,
} from './incidentsLogic.js';

/**
 * Incidents — what happened, who knows, and what is owed.
 *
 * SIX ROUTES AND ZERO REFERENCES. Before this screen the string `incidents`
 * appeared nowhere in either application, while the domain carried a complete
 * report → review → follow-up → resolve → close lifecycle with its own reach
 * rules and an append-only history.
 *
 * THE COUNT ON THIS SCREEN IS NOT AN ORGANIZATIONAL COUNT, and unlike every
 * other list in this app the server does not say so: `GET /api/incidents`
 * returns rows and nothing else — no total, no `scope`. The rows are narrowed in
 * SQL to incidents you filed plus incidents about a client on your caseload, so
 * an unreachable one is simply absent.
 *
 * So the frame is stated from the reader's OWN keys, through
 * `incidentPopulation`, and it asks for the CONJUNCTION the server actually
 * requires (`incidents:read_all` AND `directory:read_all`, at org scope) rather
 * than either half. A client-side mirror of a server predicate is normally the
 * wrong move — see `readScope.ts` — and it is admissible here only because the
 * alternative is an unlabelled count that reads as organizational and is not.
 * It fails toward the caveat: unknown and narrowed both say "not everything".
 *
 * AN EMPTY LIST IS THE DANGEROUS STATE. "No incidents" on a compliance screen in
 * a behavioural-health organization is a safety claim. For a narrowed reader it
 * is false — an incident naming only a worker, a program or a location is
 * invisible to them entirely — and the empty copy says which claim is being
 * made.
 */
/**
 * REFUSED IS NOT EMPTY, AND THE DEFAULT COPY CANNOT SAY WHY.
 *
 * `<Refused />`'s stock sentence is "your account does not have access to
 * this", which is true and does not name the SECOND key this domain requires.
 * Every route here takes `incidents:read` AND `directory:read` — an incident
 * names its reporter and the worker involved — so a reader holding the first
 * and not the second meets a 403 that the generic wording invites them to read
 * as the incidents key being missing. Naming both is the difference between an
 * administrator granting the right thing and granting the wrong one.
 *
 * And it says WITHHELD in as many words, because on a compliance register the
 * blank a refusal leaves is the same blank an all-clear leaves.
 */
const REFUSED = (
  <Refused
    heading="Incidents are not shown to you"
    message="Reading incidents needs the incidents:read permission and permission to view the workforce directory, because an incident names the person who reported it and the worker involved. This register is withheld, not empty — it is not a statement that nothing has happened."
  />
);

const STATUS_TABS = [
  { key: '' as const, label: 'All' },
  { key: 'reported' as const, label: 'Reported' },
  { key: 'under_review' as const, label: 'Under review' },
  { key: 'resolved' as const, label: 'Resolved' },
  { key: 'closed' as const, label: 'Closed' },
];

export function IncidentsCenter() {
  const permissions = usePermissions();
  const navigate = useNav();

  const [status, setStatus] = useState<incidents.IncidentStatus | ''>('');
  const [severity, setSeverity] = useState<incidents.IncidentSeverity | ''>('');

  const list = useQuery({
    queryKey: ['incidents', 'list', status, severity],
    queryFn: () =>
      incidents.list({
        ...(status === '' ? {} : { status }),
        ...(severity === '' ? {} : { severity }),
        limit: 200,
      }),
  });

  const rows = list.data ?? [];
  const phase = listPhase(list, rows.length);
  const population = incidentPopulation(permissions);
  const note = incidentScopeNote(population);

  const columns: Column<incidents.IncidentView>[] = [
    { key: 'occurred', header: 'Occurred', render: (i) => formatWhen(i.occurredAt) },
    {
      key: 'severity',
      header: 'Severity',
      render: (i) => {
        const reading = SEVERITY_READING[i.severity];
        return (
          <Pill variant={reading.variant} dot>
            {reading.label}
          </Pill>
        );
      },
    },
    { key: 'category', header: 'What', render: (i) => CATEGORY_READING[i.category] },
    { key: 'about', header: 'About', render: (i) => participantSentence(i) },
    { key: 'reporter', header: 'Filed by', render: (i) => i.reportedBy.displayName },
    {
      key: 'notified',
      header: 'Management',
      // A NEGATIVE WORTH SHOWING. The pair is enforced by the database, so this
      // is a real two-state fact and not a blank cell — and on a safety screen
      // "nobody has been told" is the row a reader is looking for.
      render: (i) => {
        const reading = notificationReading(i);
        return reading.kind === 'notified' ? (
          `Told ${formatWhen(reading.when)}`
        ) : (
          <span className="rn-muted">Not notified</span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => {
        const reading = STATUS_READING[i.status];
        return (
          <Pill variant={reading.variant} dot>
            {reading.label}
          </Pill>
        );
      },
    },
  ];

  const filtered = status !== '' || severity !== '';

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Incidents"
        blurb="What happened, who was told, and what is still owed. Every incident's narrative is a published, approved instrument — there is no free-text incident that nobody signed off the questions for."
      />

      <SettingsCard>
        <div className="cmp-control-head">
          <div className="rn-row" style={{ gap: 'var(--space-8)', flexWrap: 'wrap' }}>
            {STATUS_TABS.map((entry) => (
              <button
                key={entry.key || 'all'}
                type="button"
                className={status === entry.key ? 'rn-btn' : 'rn-btn-quiet'}
                onClick={() => setStatus(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
            <span className="rn-muted">Severity</span>
            <select
              value={severity}
              onChange={(e) =>
                setSeverity(e.currentTarget.value as incidents.IncidentSeverity | '')
              }
            >
              <option value="">Any</option>
              {(Object.keys(SEVERITY_READING) as incidents.IncidentSeverity[]).map((s) => (
                <option key={s} value={s}>
                  {SEVERITY_READING[s].label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <ListState
          phase={phase}
          surface="table"
          rows={5}
          failedMessage="We couldn’t load incidents just now. Do not read this as an organization with none — nothing here has been checked."
          onRetry={() => void list.refetch()}
          refused={REFUSED}
          empty={
            filtered ? (
              <SuccessEmpty
                heading="Nothing matches this filter"
                message="Clear the filter to see the rest. Your account may also reach fewer incidents than the organization has."
              />
            ) : (
              <SuccessEmpty
                heading={
                  population === 'organization'
                    ? 'No incidents reported'
                    : 'No incidents you can see'
                }
                message={emptyIncidentSentence(population)}
              />
            )
          }
        >
          <TableCard
            columns={columns}
            rows={rows}
            rowKey={(i) => i.id}
            onRowOpen={(i) => navigate(`/compliance/incidents/${i.id}`)}
            caption="Incidents"
          />
        </ListState>

        {note ? <ScopeNote scope={note} /> : null}
      </SettingsCard>
    </div>
  );
}
